(() => {
  if (customElements.get('joffz-header-search')) return;

  class JoffzHeaderSearch extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.dialog = this.querySelector('dialog');
      this.input = this.querySelector('[data-joffz-search-input]');
      if (!this.dialog || !this.input) return;
      this.results = this.querySelector('[data-joffz-search-results]');
      this.status = this.querySelector('[data-joffz-search-status]');
      this.initial = this.querySelector('[data-joffz-search-initial]');
      this.viewAll = this.querySelector('[data-joffz-search-all]');
      this.labels = JSON.parse(this.querySelector('[data-joffz-search-labels]').textContent);
      this.cache = new Map();
      this.sequence = 0;
      this.selected = -1;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-joffz-search-open], [data-joffz-search-entry]');
        if (!trigger || trigger.getAttribute('aria-controls') !== this.dialog.id) return;
        event.preventDefault();
        this.open(trigger);
      }, options);
      document.addEventListener('focusin', (event) => {
        const entry = event.target.closest('[data-joffz-search-entry]');
        if (entry && !this.returningFocus && entry.getAttribute('aria-controls') === this.dialog.id) this.open(entry);
      }, options);
      this.querySelector('[data-joffz-search-close]').addEventListener('click', () => this.close(), options);
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      }, options);
      this.dialog.addEventListener('close', () => this.afterClose(), options);
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) {
          const bounds = this.dialog.getBoundingClientRect();
          if (event.clientY < bounds.top || event.clientY > bounds.bottom || event.clientX < bounds.left || event.clientX > bounds.right) this.close();
        }
      }, options);
      this.dialog.addEventListener('keydown', (event) => this.onKeydown(event), options);
      this.input.addEventListener('input', (event) => {
        if (!event.isComposing) this.onInput();
      }, options);
      this.input.addEventListener('compositionend', () => this.onInput(), options);
      this.input.form.addEventListener('submit', (event) => {
        if (!this.input.value.trim()) event.preventDefault();
      }, options);
    }

    disconnectedCallback() {
      clearTimeout(this.closeTimer);
      if (this.dialog?.open) this.dialog.close();
      this.afterClose();
      this.events?.abort();
      this.events = null;
    }

    triggers() {
      return Array.from(document.querySelectorAll('[data-joffz-search-open], [data-joffz-search-entry]')).filter((trigger) => trigger.getAttribute('aria-controls') === this.dialog.id);
    }

    open(trigger) {
      if (this.dialog.open) return;
      this.lastFocus = trigger;
      this.dialog.classList.remove('is-closing');
      const entry = trigger?.matches('[data-joffz-search-entry]') ? trigger : trigger?.closest('form')?.querySelector('[data-joffz-search-entry]');
      if (entry) this.input.value = entry.value;
      this.dispatchEvent(new CustomEvent('joffz:search-open', { bubbles: true }));
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.dialog.showModal();
      this.modalOpen = true;
      this.triggers().forEach((item) => item.setAttribute('aria-expanded', 'true'));
      this.input.focus({ preventScroll: true });
      this.onInput();
    }

    close() {
      if (!this.dialog?.open || this.dialog.classList.contains('is-closing')) return;
      this.cancelSearch();
      this.dialog.classList.add('is-closing');
      this.closeTimer = setTimeout(() => {
        this.returningFocus = true;
        this.dialog.close();
        this.afterClose();
      }, matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 160);
    }

    afterClose() {
      if (!this.modalOpen) return;
      this.modalOpen = false;
      clearTimeout(this.closeTimer);
      this.dialog.classList.remove('is-closing');
      this.cancelSearch();
      document.body.style.overflow = this.previousOverflow || '';
      this.triggers().forEach((item) => { item.setAttribute('aria-expanded', 'false'); if (item.matches('[data-joffz-search-entry]')) item.value = this.input.value; });
      this.input.setAttribute('aria-expanded', 'false');
      this.returningFocus = true;
      if (this.lastFocus?.isConnected) this.lastFocus.focus({ preventScroll: true });
      this.returningFocus = false;
    }

    cancelSearch() {
      clearTimeout(this.timer);
      this.request?.abort();
      this.sequence += 1;
      this.results?.removeAttribute('aria-busy');
    }

    onInput() {
      this.cancelSearch();
      const query = this.input.value.trim();
      this.selected = -1;
      this.input.removeAttribute('aria-activedescendant');
      this.results.replaceChildren();
      this.input.setAttribute('aria-expanded', 'false');
      this.initial.hidden = Boolean(query);
      this.viewAll.hidden = !query;
      if (!query) {
        this.status.textContent = '';
        return;
      }
      const allUrl = new URL(this.dataset.searchUrl, location.origin);
      allUrl.searchParams.set('q', query);
      allUrl.searchParams.set('type', 'product');
      allUrl.searchParams.set('options[prefix]', 'last');
      this.viewAll.href = allUrl.toString();
      this.viewAll.textContent = this.labels.searchFor.replaceAll('__QUERY__', query);
      this.status.textContent = this.labels.loading;
      this.results.setAttribute('aria-busy', 'true');
      const sequence = this.sequence;
      this.timer = setTimeout(() => this.search(query, sequence), 220);
    }

    async search(query, sequence) {
      if (this.cache.has(query)) {
        this.render(this.cache.get(query), query, sequence);
        return;
      }
      this.request = new AbortController();
      const controller = this.request;
      const timeout = setTimeout(() => controller.abort('timeout'), 10000);
      try {
        const url = new URL(this.dataset.predictiveUrl, location.origin);
        url.pathname = `${url.pathname.replace(/\/$/, '').replace(/\.json$/, '')}.json`;
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product,collection,query,page,article');
        url.searchParams.set('resources[limit]', '8');
        url.searchParams.set('resources[limit_scope]', 'each');
        const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`Search ${response.status}`);
        const data = await response.json();
        if (!data.resources?.results) throw new Error('Invalid search response');
        if (this.cache.size >= 24) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(query, data.resources.results);
        this.render(data.resources.results, query, sequence);
      } catch (error) {
        if (sequence !== this.sequence || !this.dialog.open) return;
        if (controller.signal.aborted && controller.signal.reason !== 'timeout') return;
        this.results.replaceChildren();
        this.status.textContent = `${this.labels.error}. ${this.labels.searchFor.replaceAll('__QUERY__', query)}`;
        this.input.setAttribute('aria-expanded', 'false');
      } finally {
        clearTimeout(timeout);
        if (sequence === this.sequence) this.results.removeAttribute('aria-busy');
      }
    }

    safeUrl(value, image = false) {
      if (typeof value !== 'string' || !value.trim()) return null;
      try {
        const url = new URL(value, location.origin);
        if (!['http:', 'https:'].includes(url.protocol)) return null;
        if (!image && url.origin !== location.origin) return null;
        return url.toString();
      } catch {
        return null;
      }
    }

    resultLink(item, type, index) {
      const url = this.safeUrl(item.url);
      if (!url) return null;
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.id = `${this.dialog.id}-${type}-${index}`;
      anchor.setAttribute('role', 'option');
      anchor.setAttribute('aria-selected', 'false');
      if (type !== 'products') {
        anchor.textContent = item.title || item.text || '';
        return anchor;
      }
      anchor.className = 'joffz-header-search__product';
      const source = item.featured_image?.url || (typeof item.image === 'string' ? item.image : item.image?.url);
      const imageUrl = source && this.safeUrl(source, true);
      if (imageUrl) {
        const image = document.createElement('img');
        const sizedImage = new URL(imageUrl);
        sizedImage.searchParams.set('width', '420');
        image.src = sizedImage.toString();
        image.alt = '';
        image.width = 336;
        image.height = 420;
        image.loading = 'lazy';
        image.className = 'joffz-header-search__image';
        anchor.append(image);
      }
      const title = document.createElement('span');
      title.className = 'joffz-header-search__product-title';
      title.textContent = item.title;
      anchor.append(title);
      const isGalleryBuilder = Number(item.id) === 10281511616795 || item.handle === 'create-your-own-gallery-wall' || item.template_suffix === 'bundle-build' || /\/products\/create-your-own-gallery-wall\/?$/.test(new URL(url).pathname);
      if (!isGalleryBuilder && item.price !== null && item.price !== undefined && Number.isFinite(Number(item.price))) {
        const price = document.createElement('span');
        price.className = 'joffz-header-search__price';
        try {
          price.textContent = new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency: this.dataset.currency }).format(Number(item.price));
        } catch {
          price.textContent = `${item.price} ${this.dataset.currency}`;
        }
        anchor.append(price);
      }
      return anchor;
    }

    render(data, query, sequence) {
      if (sequence !== this.sequence || query !== this.input.value.trim() || !this.dialog.open) return;
      this.results.replaceChildren();
      this.results.removeAttribute('aria-busy');
      const links = document.createElement('div');
      links.className = 'joffz-header-search__links';
      const counts = [];
      for (const type of ['queries', 'collections', 'pages', 'articles', 'products']) {
        const items = Array.isArray(data[type]) ? data[type].slice(0, type === 'products' ? 8 : 4) : [];
        const list = document.createElement('ul');
        list.className = type === 'products' ? 'joffz-header-search__grid' : 'joffz-header-search__list';
        list.setAttribute('role', 'presentation');
        items.forEach((item, index) => {
          const anchor = this.resultLink(item, type, index);
          if (!anchor) return;
          const li = document.createElement('li');
          li.setAttribute('role', 'presentation');
          li.append(anchor);
          list.append(li);
        });
        if (!list.children.length) continue;
        const group = document.createElement('section');
        group.setAttribute('role', 'group');
        const heading = document.createElement('h3');
        heading.id = `${this.dialog.id}-${type}-heading`;
        heading.textContent = this.labels[type];
        group.setAttribute('aria-labelledby', heading.id);
        group.append(heading, list);
        counts.push(`${this.labels[type]}: ${list.children.length}`);
        if (type === 'products') this.results.append(group);
        else links.append(group);
      }
      if (links.children.length) this.results.prepend(links);
      this.results.dataset.productsOnly = String(!links.children.length);
      this.input.setAttribute('aria-expanded', String(Boolean(counts.length)));
      this.status.textContent = counts.length ? counts.join(' · ') : this.labels.empty.replaceAll('__QUERY__', query);
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      const suggestions = Array.from(this.results.querySelectorAll('[role="option"]'));
      if (event.target === this.input && ['ArrowDown', 'ArrowUp'].includes(event.key) && suggestions.length) {
        event.preventDefault();
        this.selected = this.selected < 0
          ? (event.key === 'ArrowDown' ? 0 : suggestions.length - 1)
          : (this.selected + (event.key === 'ArrowDown' ? 1 : -1) + suggestions.length) % suggestions.length;
        suggestions.forEach((item, index) => item.setAttribute('aria-selected', String(index === this.selected)));
        const active = suggestions[this.selected];
        this.input.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
        return;
      }
      if (event.target === this.input && event.key === 'Enter' && !event.isComposing && this.selected >= 0) {
        const active = suggestions[this.selected];
        if (active) {
          event.preventDefault();
          active.click();
        }
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(this.dialog.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), [tabindex="0"]')).filter((item) => item.getClientRects().length && !item.closest('[hidden]'));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
  }

  customElements.define('joffz-header-search', JoffzHeaderSearch);
})();

